import { LOCALES, type Locale } from '@eklevo/shared/i18n/backoffice'
import type { ReactNode } from 'react'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { useI18n } from '../lib/i18n'

/*
 * The back office's own pieces of page: always Eklevo's brand, never a company's (ADR 0007 does
 * not apply here), and its own texts. Not in the Figma's detail: sober, marked for design review.
 */

/** «Eklevo» and «Panel de administración», as the Figma's header (86:2451). */
export function StaffBrand() {
  const { t } = useI18n()
  return (
    <span className="flex items-baseline gap-3">
      <span className="text-xl font-medium tracking-tight">{t('app.name')}</span>
      <span className="border-l border-line pl-3 text-sm text-ink-muted">{t('app.panel')}</span>
    </span>
  )
}

export function LanguageSelect() {
  const { t, locale, setLocale } = useI18n()
  return (
    <select
      aria-label={t('common.language')}
      className="rounded-lg border border-line bg-surface px-2 py-1.5 text-sm"
      value={locale}
      onChange={(e) => setLocale(e.target.value as Locale)}
    >
      {LOCALES.map((l) => (
        <option key={l} value={l}>
          {l.toUpperCase()}
        </option>
      ))}
    </select>
  )
}

export function Footer() {
  const { t } = useI18n()
  return (
    <footer className="border-t border-line bg-surface">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-sm text-ink-muted sm:px-6">
        <p>{t('footer.rights', { year: new Date().getFullYear() })}</p>
        <a className="hover:text-ink" href="mailto:hola@eklevo.com">
          {t('footer.help')}
        </a>
      </div>
    </footer>
  )
}

export function Loading() {
  const { t } = useI18n()
  return (
    <p role="status" className="py-16 text-center text-ink-muted">
      {t('common.loading')}
    </p>
  )
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  const { t } = useI18n()
  return (
    <Card role="alert" className="text-center">
      <p>{t('common.error.generic')}</p>
      {onRetry && (
        <Button variant="secondary" className="mt-4" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </Card>
  )
}

/** A list or a screen with nothing to show yet, or nothing that matches its filters. */
export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Card className="text-center">
      <p className="text-lg">{title}</p>
      {children && <div className="mt-2 text-sm text-ink-muted">{children}</div>}
    </Card>
  )
}

/** A screen's title and what it shows, under the header. */
export function PageHeading({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div>
      <h1 className="text-2xl [overflow-wrap:anywhere]">{title}</h1>
      {children && <p className="mt-2 max-w-3xl text-ink-muted">{children}</p>}
    </div>
  )
}

/** A message that explains a failure, read out as it appears; empty, it takes no room. */
export function Problem({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-sm text-danger not-empty:mt-4">
      {children}
    </p>
  )
}
